import { useEffect, useRef, type RefObject } from 'react';
import * as dashjs from 'dashjs';
import { usePlayerStore } from '@/store/usePlayerStore';
import type { LevelInfo } from '@/store/usePlayerStore';

/**
 * Module-level reference to the active dash.js player instance.
 * Used by LiveButton (seek to live) and SettingsMenu (quality switching).
 * Only one player exists at a time so this is safe.
 */
let activeDashPlayer: dashjs.MediaPlayerClass | null = null;

export function getActiveDashPlayer(): dashjs.MediaPlayerClass | null {
  return activeDashPlayer;
}

export interface UseDashResult {
  reload: () => void;
}

function levelInfoFromRepresentation(rep: dashjs.Representation): LevelInfo {
  return {
    height: rep.height,
    bitrate: rep.bitrateInKbit * 1000,
    name: rep.height ? `${rep.height}p` : `${rep.bitrateInKbit} kbps`,
  };
}

/**
 * Attach a DASH (.mpd) stream to a <video> element using dash.js.
 *
 * Follows the same pattern as useHls: creates a dash.js player, writes
 * playback state to the Zustand player store, and tears down on unmount
 * or channel switch.
 *
 * CORS handling: a request interceptor rewrites all outbound requests
 * to go through the /proxy endpoint, which adds permissive CORS headers.
 */
export function useDash(
  videoRef: RefObject<HTMLVideoElement | null>,
  url: string | null
): UseDashResult {
  const dashRef = useRef<dashjs.MediaPlayerClass | null>(null);
  const setError = usePlayerStore((s) => s.setError);
  const reloadKey = usePlayerStore((s) => s.reloadKey);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url) return;

    // Create a fresh dash.js player instance for each channel switch.
    const player = dashjs.MediaPlayer().create();
    dashRef.current = player;
    activeDashPlayer = player;

    // Configure live-friendly settings.
    player.updateSettings({
      debug: {
        logLevel: dashjs.Debug.LOG_LEVEL_NONE,
      },
      streaming: {
        delay: {
          liveDelay: 3,
        },
        liveCatchup: {
          enabled: true,
          maxDrift: 0.5,
          playbackRate: { min: -0.5, max: 0.5 },
          playbackBufferMin: 0.5,
        },
        buffer: {
          fastSwitchEnabled: true,
        },
      },
    });

    // CORS proxy: rewrite every outbound request URL to go through
    // our Cloudflare Pages proxy so CORS headers are added.
    const proxyBase = `${location.origin}/proxy`;
    player.addRequestInterceptor((request) => {
      const originalUrl = request.url;
      if (originalUrl && !originalUrl.startsWith(proxyBase)) {
        request.url = `${proxyBase}?url=${encodeURIComponent(originalUrl)}`;
      }
      return Promise.resolve(request);
    });

    // ── Event handlers ──

    const handleStreamInitialized = () => {
      // Populate quality levels once the stream is ready.
      const reps = player.getRepresentationsByType('video');
      if (reps && reps.length > 0) {
        const levels: LevelInfo[] = reps.map((r) => levelInfoFromRepresentation(r));
        usePlayerStore.getState().setLevels(levels);
        usePlayerStore.getState().setCurrentLevel(-1); // auto
      }

      // Detect live vs VOD.
      const isDynamic = player.isDynamic();
      usePlayerStore.getState().setLive(isDynamic);
    };

    const handleError = (e: dashjs.MediaPlayerEvent) => {
      const evt = e as { error?: { code?: number; message?: string } };
      const code = evt.error?.code ?? 0;
      const msg = evt.error?.message || '';

      const type: 'cors' | 'network' | 'unknown' = 'network';
      let message = 'Stream failed.';

      if (code === 10 || code === 11 || code === 25) {
        message =
          "The stream's main playlist couldn't be read — the broadcaster may be offline, " +
          'the URL may be outdated, or the CDN is returning an error page.';
      } else if (code === 17 || code === 27 || code === 28) {
        message =
          "Couldn't fetch stream data — the broadcaster may be offline, blocking playback, or behind an unsupported CDN. Try a different channel.";
      } else if (code === 23) {
        message = 'This browser does not support DASH playback. Try a different channel.';
      } else if (msg) {
        message = `Stream error: ${msg}`;
      }

      setError({ message, type });
    };

    const handleQualityChange = () => {
      const reps = player.getRepresentationsByType('video');
      if (reps && reps.length > 0) {
        const levels: LevelInfo[] = reps.map((r) => levelInfoFromRepresentation(r));
        usePlayerStore.getState().setLevels(levels);
      }
    };

    const handleTimeUpdate = () => {
      usePlayerStore.getState().setCurrentTime(player.time());
      const latency = player.getCurrentLiveLatency();
      if (latency > 0) {
        usePlayerStore.getState().setLatency(Math.round(latency));
      }
    };

    const handleDurationChange = () => {
      const dur = player.duration();
      usePlayerStore.getState().setDuration(dur);
    };

    // Register event handlers.
    player.on(dashjs.MediaPlayer.events.STREAM_INITIALIZED, handleStreamInitialized);
    player.on(dashjs.MediaPlayer.events.ERROR, handleError);
    player.on(dashjs.MediaPlayer.events.QUALITY_CHANGE_RENDERED, handleQualityChange);
    player.on(dashjs.MediaPlayer.events.PLAYBACK_TIME_UPDATED, handleTimeUpdate);
    player.on(dashjs.MediaPlayer.events.PLAYBACK_METADATA_LOADED, handleDurationChange);

    // Also surface native video errors (e.g. decoding, source not supported).
    const handleNativeError = () => {
      const err = video.error;
      if (!err) return;
      let message = 'Stream failed.';
      switch (err.code) {
        case 1:
          message = 'Stream playback was interrupted.';
          break;
        case 2:
          message =
            'Network error reaching the broadcaster. The stream may be offline or the proxy is unreachable.';
          break;
        case 3:
          message = 'Stream decoding error. The broadcaster may have changed the format.';
          break;
        case 4:
          message =
            "The stream's source isn't supported. The broadcaster may be offline, or its server has an invalid SSL certificate.";
          break;
      }
      setError({ message, type: 'network' });
    };
    video.addEventListener('error', handleNativeError);

    // Initialize and start playback.
    player.initialize(video, url, true);

    return () => {
      video.removeEventListener('error', handleNativeError);
      player.off(dashjs.MediaPlayer.events.STREAM_INITIALIZED, handleStreamInitialized);
      player.off(dashjs.MediaPlayer.events.ERROR, handleError);
      player.off(dashjs.MediaPlayer.events.QUALITY_CHANGE_RENDERED, handleQualityChange);
      player.off(dashjs.MediaPlayer.events.PLAYBACK_TIME_UPDATED, handleTimeUpdate);
      player.off(dashjs.MediaPlayer.events.PLAYBACK_METADATA_LOADED, handleDurationChange);
      player.reset();
      dashRef.current = null;
      activeDashPlayer = null;
    };
  }, [videoRef, url, reloadKey, setError]);

  return {
    reload: () => usePlayerStore.getState().triggerReload(),
  };
}
